import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  DocumentData,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  QuerySnapshot,
  startAfter,
  Timestamp,
  updateDoc,
  where,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, storage } from '../utils/firebaseConfig';
import { City } from './cityService';
import { activityService } from './activityService';
import { userService } from './userService';

export interface LocationData {
  name: string;
  address: string;
  coordinates: {
    latitude: number;
    longitude: number;
  };
  city: string;
}

export interface Event {
  id?: string;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  startTime: string; // Format: HH:mm (e.g., "14:30")
  endTime: string; // Format: HH:mm (e.g., "16:00")
  // Legacy support - keeping for backward compatibility
  date?: string;
  location?: LocationData;
  city?: City; // City ID reference
  image?: string;
  isFree: boolean;
  cost?: number;
  tags?: string[];
  category: string;
  interested?: number;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
  createdBy?: string;
  createdByName?: string;
  createdByEmail?: string;
  createdByAvatar?: string;
  website?: string | null;
  linkPreview?: {
    title?: string;
    description?: string;
    favicon?: string;
    image?: string;
  } | null;

  // Boosting fields (for future paid promotion)
  isBoosted?: boolean;
  boostLevel?: 'standard' | 'premium' | 'featured';
  boostUntil?: Timestamp;
  boostPaidBy?: string;     // userId who paid
  boostPaidAt?: Timestamp;

  // Activity tracking
  lastUpdatedFields?: string[];  // Track what changed in last update
  interestedUsers?: string[];    // Array of user IDs (for quick lookup)
}

export interface EventFilters {
  isFree?: boolean;
  tags?: string[];
  location?: string;
  category?: string;
  city?: string;
  dateRange?: {
    start: Date;
    end: Date;
  };
}

export interface PaginationOptions {
  limit?: number;
  lastDoc?: any;
}

class EventService {
  private readonly COLLECTION_NAME = 'events';
  private readonly STORAGE_PATH = 'events';

  /**
   * Convert Date object to yyyy-mm-dd string format
   */
  private formatDateToString(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  async fetchEvents(filters?: EventFilters, pagination?: PaginationOptions): Promise<{
    events: Event[];
    hasMore: boolean;
    lastDoc: any;
  }> {
    try {
      let q = query(collection(db, this.COLLECTION_NAME));

      // Filter out past events - only show events that haven't ended yet
      const today = this.formatDateToString(new Date());
      q = query(q, where('endDate', '>=', today));

      // Apply filters
      if (filters?.isFree !== undefined) {
        q = query(q, where('isFree', '==', filters.isFree));
      }

      if (filters?.tags && filters.tags.length > 0) {
        q = query(q, where('tags', 'array-contains-any', filters.tags));
      }

      if (filters?.location) {
        q = query(q, where('location', '>=', filters.location), where('location', '<=', filters.location + '\uf8ff'));
      }

      if (filters?.category) {
        q = query(q, where('category', '==', filters.category));
      }

      if (filters?.city) {
        q = query(q, where('city.id', '==', filters.city));
      }

      if (filters?.dateRange) {
        // Convert Date objects to yyyy-mm-dd format for comparison
        const filterStartDate = this.formatDateToString(filters.dateRange.start);
        const filterEndDate = this.formatDateToString(filters.dateRange.end);
        // Filter events where the event's date range overlaps with the filter's date range
        // An event overlaps if: event.startDate <= filterEndDate AND event.endDate >= filterStartDate
        q = query(q, where('startDate', '<=', filterEndDate), where('endDate', '>=', filterStartDate));
      }

      // Order by creation date (newest first)
      q = query(q, orderBy('createdAt', 'desc'));

      // Apply pagination
      const pageLimit = pagination?.limit || 10;
      q = query(q, limit(pageLimit + 1)); // Fetch one extra to check if there are more

      if (pagination?.lastDoc) {
        q = query(q, startAfter(pagination.lastDoc));
      }

      const snapshot = await getDocs(q);
      const docs = snapshot.docs;
      
      const hasMore = docs.length > pageLimit;
      const events = docs.slice(0, pageLimit).map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Event[];

      const lastDoc = hasMore ? docs[pageLimit - 1] : null;

      return { events, hasMore, lastDoc };
    } catch (error) {
      console.error('Error fetching events:', error);
      throw new Error('Failed to fetch events');
    }
  }

  async fetchEventById(id: string): Promise<Event | null> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        } as Event;
      }

      return null;
    } catch (error) {
      console.error('Error fetching event:', error);
      throw new Error('Failed to fetch event');
    }
  }

  async createEvent(
    eventData: Omit<Event, 'id' | 'createdAt' | 'updatedAt'>,
    createActivity: boolean = true
  ): Promise<string> {
    try {
      const now = Timestamp.now();
      const docRef = await addDoc(collection(db, this.COLLECTION_NAME), {
        ...eventData,
        createdAt: now,
        updatedAt: now,
        interested: eventData.interested || 0,
        interestedUsers: eventData.interestedUsers || []
      });

      const eventId = docRef.id;

      // Create activity for event creation only if user enabled sharing
      if (createActivity && eventData.createdBy) {
        const userProfile = await userService.getUserProfile(eventData.createdBy);
        const shouldShare = userProfile?.shareEventCreation !== false;

        if (shouldShare) {
          await activityService.createActivity({
            type: 'event_created',
            userId: eventData.createdBy,
            userName: eventData.createdByName || 'Unknown',
            userAvatar: eventData.createdByAvatar,
            eventId: eventId,
            eventTitle: eventData.title,
            eventCategory: eventData.category,
            eventImage: eventData.image,
            metadata: {
              eventDate: eventData.startDate && eventData.endDate && eventData.startDate !== eventData.endDate
                ? `${new Date(eventData.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(eventData.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                : eventData.startDate
                  ? new Date(eventData.startDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
                  : eventData.date || undefined,
              eventLocation: eventData.location?.name,
              eventPrice: eventData.isFree ? 'free' : eventData.cost ? String(eventData.cost) : undefined
            }
          });
        }
      }

      return eventId;
    } catch (error) {
      console.error('Error creating event:', error);
      throw new Error('Failed to create event');
    }
  }

  async updateEvent(
    id: string,
    eventData: Partial<Event>,
    createActivity: boolean = true
  ): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, id);

      // Track which fields are being updated
      const updatedFields = Object.keys(eventData).filter(
        key => !['updatedAt', 'lastUpdatedFields'].includes(key)
      );

      await updateDoc(docRef, {
        ...eventData,
        updatedAt: Timestamp.now(),
        lastUpdatedFields: updatedFields
      });

      // Create activity for significant updates only if user enabled sharing
      if (createActivity && updatedFields.length > 0 && !updatedFields.includes('interested')) {
        const event = await this.fetchEventById(id);
        if (event && event.createdBy) {
          const userProfile = await userService.getUserProfile(event.createdBy);
          const shouldShare = userProfile?.shareEventUpdates !== false;

          if (shouldShare) {
            await activityService.createActivity({
              type: 'event_updated',
              userId: event.createdBy,
              userName: event.createdByName || 'Unknown',
              userAvatar: event.createdByAvatar,
              eventId: id,
              eventTitle: event.title,
              eventCategory: event.category,
              eventImage: event.image,
              metadata: {
                updateFields: updatedFields,
                eventDate: event.startDate && event.endDate && event.startDate !== event.endDate
                  ? `${new Date(event.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(event.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                  : event.startDate
                    ? new Date(event.startDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
                    : event.date || undefined,
                eventLocation: event.location?.name,
                eventPrice: event.isFree ? 'free' : event.cost ? String(event.cost) : undefined
              }
            });
          }
        }
      }
    } catch (error) {
      console.error('Error updating event:', error);
      throw new Error('Failed to update event');
    }
  }

  async deleteEvent(id: string): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting event:', error);
      throw new Error('Failed to delete event');
    }
  }

  async uploadEventImage(eventId: string, imageFile: File | Blob, fileName: string): Promise<string> {
    try {
      const timestamp = Date.now();
      const imageRef = ref(storage, `${this.STORAGE_PATH}/${eventId}/${timestamp}_${fileName}`);
      
      await uploadBytes(imageRef, imageFile);
      const downloadURL = await getDownloadURL(imageRef);
      
      return downloadURL;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw new Error('Failed to upload image');
    }
  }

  async deleteEventImage(imageUrl: string): Promise<void> {
    try {
      const imageRef = ref(storage, imageUrl);
      await deleteObject(imageRef);
    } catch (error) {
      console.error('Error deleting image:', error);
      throw new Error('Failed to delete image');
    }
  }

  subscribeToEvents(
    callback: (events: Event[]) => void,
    filters?: EventFilters,
    limitCount: number = 20
  ): () => void {
    let q = query(collection(db, this.COLLECTION_NAME));

    // Filter out past events - only show events that haven't ended yet
    const today = this.formatDateToString(new Date());
    q = query(q, where('endDate', '>=', today));

    // Apply filters
    if (filters?.isFree !== undefined) {
      q = query(q, where('isFree', '==', filters.isFree));
    }

    if (filters?.tags && filters.tags.length > 0) {
      q = query(q, where('tags', 'array-contains-any', filters.tags));
    }

    if (filters?.category) {
      q = query(q, where('category', '==', filters.category));
    }

    if (filters?.city) {
      q = query(q, where('city.id', '==', filters.city));
    }

    if (filters?.dateRange) {
      // Convert Date objects to yyyy-mm-dd format for comparison
      const filterStartDate = this.formatDateToString(filters.dateRange.start);
      const filterEndDate = this.formatDateToString(filters.dateRange.end);
      // Filter events where the event's date range overlaps with the filter's date range
      // An event overlaps if: event.startDate <= filterEndDate AND event.endDate >= filterStartDate
      q = query(q, where('startDate', '<=', filterEndDate), where('endDate', '>=', filterStartDate));
    }

    // Order and limit
    q = query(q, orderBy('createdAt', 'desc'), limit(limitCount));

    const unsubscribe = onSnapshot(q, (snapshot: QuerySnapshot<DocumentData>) => {
      const events = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Event[];

      callback(events);
    }, (error) => {
      console.error('Error in events subscription:', error);
    });

    return unsubscribe;
  }

  async incrementInterested(eventId: string): Promise<void> {
    try {
      const event = await this.fetchEventById(eventId);
      if (event) {
        const newInterested = (event.interested || 0) + 1;
        await this.updateEvent(eventId, { interested: newInterested }, false); // Don't create activity for count change
      }
    } catch (error) {
      console.error('Error incrementing interested:', error);
      throw new Error('Failed to update interest count');
    }
  }

  /**
   * Toggle user's interest in an event and create activity
   */
  async toggleInterested(
    eventId: string,
    userId: string,
    userName: string,
    userAvatar?: string
  ): Promise<boolean> {
    try {
      const event = await this.fetchEventById(eventId);
      if (!event) {
        throw new Error('Event not found');
      }

      const interestedUsers = event.interestedUsers || [];
      const isCurrentlyInterested = interestedUsers.includes(userId);

      if (isCurrentlyInterested) {
        // Remove from interested users
        const docRef = doc(db, this.COLLECTION_NAME, eventId);
        await updateDoc(docRef, {
          interestedUsers: arrayRemove(userId),
          interested: (event.interested || 1) - 1,
          updatedAt: Timestamp.now()
        });
        return false;
      } else {
        // Add to interested users
        const docRef = doc(db, this.COLLECTION_NAME, eventId);
        await updateDoc(docRef, {
          interestedUsers: arrayUnion(userId),
          interested: (event.interested || 0) + 1,
          updatedAt: Timestamp.now()
        });

        // Create activity for showing interest only if user enabled sharing
        const userProfile = await userService.getUserProfile(userId);
        const shouldShare = userProfile?.shareEventInterested !== false;

        if (shouldShare) {
          await activityService.createActivity({
            type: 'event_interested',
            userId: userId,
            userName: userName,
            userAvatar: userAvatar,
            eventId: eventId,
            eventTitle: event.title,
            eventCategory: event.category,
            eventImage: event.image,
            metadata: {
              eventDate: event.startDate && event.endDate && event.startDate !== event.endDate
                ? `${new Date(event.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(event.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                : event.startDate
                  ? new Date(event.startDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
                  : event.date || undefined,
              eventLocation: event.location?.name,
              eventPrice: event.isFree ? 'free' : event.cost ? String(event.cost) : undefined
            }
          });
        }

        return true;
      }
    } catch (error) {
      console.error('Error toggling interested:', error);
      throw new Error('Failed to toggle interest');
    }
  }

  async searchEvents(searchTerm: string, filters?: EventFilters): Promise<Event[]> {
    try {
      // Note: Firestore doesn't support full-text search natively
      // This is a simple implementation that searches in title and description
      // For production, consider using Algolia or another search service

      // fetchEvents already filters out past events
      const { events } = await this.fetchEvents(filters, { limit: 100 });

      const searchLower = searchTerm.toLowerCase();
      return events.filter(event =>
        event.title.toLowerCase().includes(searchLower) ||
        event.description.toLowerCase().includes(searchLower)
      );
    } catch (error) {
      console.error('Error searching events:', error);
      throw new Error('Failed to search events');
    }
  }
}

export const eventService = new EventService();